import { getTranslationValue } from '@/utils/translations';
import {
  HubCardType,
  LowestPerformingCustomerDataType,
  LowestPerformingCustomersHubCardDataItem,
  LowestPerformingCustomersHubCardData as RawLowestPerformingCustomersHubCardData,
} from '@/types';
import {
  LowestPerformingCustomerRow,
  LowestPerformingCustomersHubCardData,
} from '@/experiences/sales-portal/domain/insights/blocs/types/insight';
import { toFixedDecimalPercentage } from '@/utils/percentage';

const toCustomerRow = (
  raw: LowestPerformingCustomersHubCardDataItem,
): LowestPerformingCustomerRow => ({
  formattedValue:
    raw.type === LowestPerformingCustomerDataType.Percentage
      ? toFixedDecimalPercentage(raw.value)
      : raw.value.toFixed(0),
  restaurantName: getTranslationValue(raw.restaurant_name),
  restaurantUUID: raw.restaurant_uuid,
  subtitle: getTranslationValue(raw.subtitle),
});

/**
 * Transforms insight hub card data returned by `getHubCardData` into the
 * domain {@link LowestPerformingCustomersHubCardData} presentation data.
 *
 * @param {string[]} cardDataJson - Stringified
 *   `LowestPerformingCustomersHubCardData` entries from `getHubCardData`.
 * @param {boolean} isGrossProfitDisabled - boolean flag returned by backend used to hide gross profit data
 *
 * @returns The {@link LowestPerformingCustomersHubCardData} data, or `null`
 *   when the payload is empty, not a lowest performing customers card, or has
 *   no customers in either list (which will hide the card).
 */
export const transformLowestPerformingCustomers = (
  cardDataJson: string[],
  isGrossProfitDisabled: boolean,
): LowestPerformingCustomersHubCardData | null => {
  const [json] = cardDataJson;
  if (!json) {
    return null;
  }

  const raw = JSON.parse(json) as RawLowestPerformingCustomersHubCardData;

  // hide card if card_type mismatch
  if (raw.card_type !== HubCardType.LowestPerformingCustomers) {
    return null;
  }

  const byGpPercentage = isGrossProfitDisabled
    ? []
    : (raw.card_data?.avg_gp_percentage?.items ?? []).map(toCustomerRow);
  const byUnitsSold = (raw.card_data?.units_sold?.items ?? []).map(
    toCustomerRow,
  );

  // hide card when there are no customers to show
  if (byGpPercentage.length === 0 && byUnitsSold.length === 0) {
    return null;
  }

  return {
    byGpPercentage,
    byUnitsSold,
    title: getTranslationValue(raw.title),
  };
};
